"use client";

import dayjs from "dayjs";
import { memo, useCallback, useMemo, useState } from "react";
import { useController, useWatch, type Control } from "react-hook-form";
import { Typography } from "@/components/Typography";
import SearchLocationField from "@/components/search-form/fields/SearchLocationField";
import type { LocationComboboxOption } from "@/components/LocationCombobox";
import { useLocationSuggestions } from "@/hooks/search-form/useLocationSuggestions";
import {
  type BookForMeFormData,
  parseDateRange,
  parseGuests,
  toApiDate,
} from "../book-for-me.type";

interface BookForMeDestinationFieldProps {
  control: Control<BookForMeFormData>;
}

const noop = () => undefined;

/**
 * Destination search (popular destinations + hotel-name suggestions),
 * shared with SearchModule via SearchLocationField / useLocationSuggestions.
 */
const BookForMeDestinationField = ({
  control,
}: BookForMeDestinationFieldProps) => {
  const {
    field: { value: destinationId, onChange: setDestinationId },
    fieldState: { error },
  } = useController({ control, name: "destinationId" });
  const {
    field: { onChange: setDestinationLabel },
  } = useController({ control, name: "destination" });

  // Typed text / selected hotel name shown in the input
  const [search, setSearch] = useState("");

  const [dateValue, guestsValue] = useWatch({
    control,
    name: ["date", "guests"],
  });

  // Hotel suggestions need dates & guests; fall back to today/tomorrow
  const suggestionParams = useMemo(() => {
    const { from, to } = parseDateRange(dateValue);
    const start = from ?? dayjs();
    const end = to && to.isAfter(start, "day") ? to : start.add(1, "day");
    const guests = parseGuests(guestsValue);

    return {
      start: toApiDate(start),
      end: toApiDate(end),
      adults: guests.adults || 2,
      children: guests.children,
      rooms: guests.rooms || 1,
    };
  }, [dateValue, guestsValue]);

  const { comboboxOptions, isComboboxLoading, sectionTitle } =
    useLocationSuggestions({
      svcType: "hotel",
      search,
      enableHotelSuggestions: true,
      ...suggestionParams,
    });

  const handleInputChange = useCallback(
    (value: string) => {
      setSearch(value);
      setDestinationId("");
      setDestinationLabel("");
    },
    [setDestinationId, setDestinationLabel],
  );

  const handleChange = useCallback(
    (value: string, option?: LocationComboboxOption) => {
      const isHotel = option?.kind === "hotel";
      // Hotel suggestion -> use the hotel's province as destination
      setSearch(isHotel ? option.label : "");
      setDestinationId(isHotel ? (option.provinceId ?? "") : value);
      setDestinationLabel(option?.label ?? "");
    },
    [setDestinationId, setDestinationLabel],
  );

  return (
    <div className="flex flex-col gap-1.5">
      <Typography variant="button-sm">Destination</Typography>
      <SearchLocationField
        isHotel
        rounded="normal"
        hideErrorTooltip
        value={search || destinationId}
        hasCoordinates={false}
        isFetchingLocation={false}
        hasError={Boolean(error)}
        showCurrentLocation={false}
        isLoadingOptions={isComboboxLoading}
        options={comboboxOptions}
        sectionTitle={sectionTitle}
        useInlineFieldExpansion={false}
        placeholder="Where do you want to stay?"
        onChange={handleChange}
        onInputChange={handleInputChange}
        onUseCurrentLocation={noop}
      />
      {error && (
        <Typography variant="body2" className="text-error-foreground">
          {error.message}
        </Typography>
      )}
    </div>
  );
};

export default memo(BookForMeDestinationField);
